//avatarCharacter.ts — avatar.xrscape.ai's characters: an image and the exact prompt Lucy reads

import { CHARACTER_TEMPLATE, type Character } from './character'
import { customImagePathFor, isValidId } from './customGarment'

export interface AvatarCharacter {
	readonly id: string
	readonly name: string
	readonly prompt: string
	readonly imageId: string | null
}

//Decart's rough cap for Lucy 2.5 ("about 750 characters"; the exact limit depends on wording),
//so the admin warns past it. The hard cap only stops junk: Decart itself rejects what will not fit.
export const PROMPT_LIMIT = 750
const PROMPT_HARD_LIMIT = 2000
const MAX_CHARACTERS = 100

//Decart warns against an empty prompt even with a strong reference, so new characters start here.
export const DEFAULT_PROMPT = `${CHARACTER_TEMPLATE} the character in the reference image.`

//The live channel: GET it as a WebSocket and every admin save arrives as the full list.
export const LIVE_PATH = '/api/characters/live'
export const LIVE_PING = 'ping'
export const LIVE_PONG = 'pong'

export const avatarToCharacter = (avatar: AvatarCharacter): Character => ({
	id: avatar.id,
	name: avatar.name,
	description: '',
	prompt: avatar.prompt,
	imageURL: avatar.imageId ? customImagePathFor(avatar.imageId) : null,
})

//Boundary validation for the admin save: the endpoint is reachable by anyone with the URL.
export function parseAvatarCharacters(value: unknown): AvatarCharacter[] {
	if (!Array.isArray(value)) throw new Error('Expected an array of characters')
	if (value.length > MAX_CHARACTERS) throw new Error(`Too many characters (max ${MAX_CHARACTERS})`)
	return value.map((entry, index) => {
		const item = (entry ?? {}) as Record<string, unknown>
		const label = `Character ${index + 1}`
		if (!isValidId(item.id)) throw new Error(`${label}: bad id`)
		if (typeof item.name !== 'string' || !item.name.trim() || item.name.length > 80)
			throw new Error(`${label}: name must be 1-80 characters`)
		if (typeof item.prompt !== 'string' || !item.prompt.trim() || item.prompt.length > PROMPT_HARD_LIMIT)
			throw new Error(`${label}: prompt must be 1-${PROMPT_HARD_LIMIT} characters`)
		if (item.imageId !== null && !isValidId(item.imageId)) throw new Error(`${label}: bad imageId`)
		return { id: item.id, name: item.name.trim(), prompt: item.prompt.trim(), imageId: item.imageId as string | null }
	})
}
